import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import { getPricingTimeByIdUseCase } from "../domain/usecases/getPricingTimeByIdUseCase";
import { updatePricingTimeUseCase } from "../domain/usecases/updatePricingTimeUseCase";
import Topbar from "../../../components/topbar/TopBar";

const DAYS = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miercoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sabado" },
  { value: 7, label: "Domingo" },
];

function createForm(pricingTime) {
  return {
    id: pricingTime?.id || "",
    name: pricingTime?.name || "",
    dayOfWeek: pricingTime?.dayOfWeek || 1,
    startTime: pricingTime?.startTime?.slice(0, 5) || "",
    endTime: pricingTime?.endTime?.slice(0, 5) || "",
    multiplier:
      pricingTime?.multiplier === null || pricingTime?.multiplier === undefined
        ? ""
        : String(pricingTime.multiplier),
  };
}

function normalizeTime(value) {
  if (!value) {
    return "";
  }

  return value.length === 5 ? `${value}:00` : value;
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";
  return message.includes("success") || message.includes("updated");
}

export default function PricingTimeUpdatePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(createForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  useEffect(() => {
    const loadPricingTime = async () => {
      try {
        setLoading(true);
        const pricingTime = await getPricingTimeByIdUseCase(id);
        setForm(createForm(pricingTime));
      } catch (error) {
        setMessage({
          open: true,
          type: "error",
          text: error.message || "No se pudo cargar el horario.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadPricingTime();
  }, [id]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    const payload = {
      id: form.id,
      name: form.name,
      dayOfWeek: Number(form.dayOfWeek),
      startTime: normalizeTime(form.startTime),
      endTime: normalizeTime(form.endTime),
      multiplier: Number(form.multiplier),
    };

    try {
      setSaving(true);
      const response = await updatePricingTimeUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error(
          response?.message || "No se pudo actualizar el horario.",
        );
      }

      setMessage({
        open: true,
        type: "success",
        text: "Horario actualizado correctamente.",
      });

      window.setTimeout(() => {
        navigate("/admin/precio-horario/lista");
      }, 1200);
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo actualizar el horario.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": {
              color: "#000",
            },
            p: 1,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 760,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  py: { xs: 2.5, md: 3 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ color: "#fff !important" }}
                    >
                      ACTUALIZAR HORARIO
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/admin/precio-horario/lista")}
                    sx={{
                      px: 2.25,
                      py: 1,
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.16)",
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.24)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Volver
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 2, md: 4 } }}>
                {loading ? (
                  <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography color="text.secondary">
                      Cargando horario...
                    </Typography>
                  </Stack>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      mx: "auto",
                      maxWidth: 480,
                      borderRadius: 3,
                      border: "1px solid rgba(20, 83, 45, 0.12)",
                      p: { xs: 1, md: 2 },
                    }}
                  >
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Nombre"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                      />
                      <TextField
                        fullWidth
                        select
                        label="Dia de la semana"
                        name="dayOfWeek"
                        value={form.dayOfWeek}
                        onChange={handleChange}
                      >
                        {DAYS.map((day) => (
                          <MenuItem key={day.value} value={day.value}>
                            {day.value} - {day.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        type="time"
                        label="Hora de inicio"
                        name="startTime"
                        value={form.startTime}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        fullWidth
                        type="time"
                        label="Hora de fin"
                        name="endTime"
                        value={form.endTime}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Multiplicador"
                        name="multiplier"
                        value={form.multiplier}
                        onChange={handleChange}
                        inputProps={{ step: "0.1", min: "0" }}
                      />

                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                        sx={{
                          mt: 1,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 700,
                          bgcolor: "#165c53",
                          color: "#fff",
                          "&:hover": {
                            bgcolor: "#12483f",
                          },
                        }}
                      >
                        {saving ? "Actualizando..." : "Actualizar"}
                      </Button>
                    </Stack>
                  </Paper>
                )}
              </Box>
            </Paper>
          </Stack>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
}
